import { useState } from 'react';

import { useDeleteMcpConnector, useMcpConnector } from '../hooks/use-mcp-connectors';

export interface McpConnectorDeleteDialogProps {
  connectorId: string;
  onClose: () => void;
}

/**
 * Sprint A4 S1 PR 3 — Task 13. Dialog modal de confirmação de remoção.
 *
 * Consome `useMcpConnector(id)` (detail com version) + `useDeleteMcpConnector`.
 * Backend faz soft-delete (status='disabled' preserva audit + destroy
 * credentialsRef no Vault). Aviso explícito sobre destruição da chave +
 * regra do silêncio (último conector → capability auto-suspended).
 */
export function McpConnectorDeleteDialog({
  connectorId,
  onClose,
}: McpConnectorDeleteDialogProps): JSX.Element | null {
  const { data: connector } = useMcpConnector(connectorId);
  const deleteMutation = useDeleteMcpConnector();
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (connector === undefined) return null;

  function handleConfirm(): void {
    if (connector === undefined) return;
    setSubmitError(null);
    deleteMutation.mutate(
      { id: connectorId, version: connector.version },
      {
        onSuccess: () => {
          onClose();
        },
        onError: (err: Error) => {
          setSubmitError(err.message);
        },
      },
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-surface p-6">
        <h2 className="text-lg font-semibold text-text-primary">Remover conector?</h2>
        <p className="mt-2 text-sm text-text-secondary">
          O conector <strong>{connector.displayName}</strong> será removido. Esta ação registra um
          audit event e destrói a credencial no cofre.
        </p>
        <p className="mt-2 text-sm text-text-secondary">
          Se for o último conector cadastrado, a feature de integrações externas será
          automaticamente desligada (regra do silêncio).
        </p>

        {submitError !== null && (
          <div className="mt-4 rounded bg-red-50 p-3 text-xs text-red-900">
            <strong>Erro:</strong> {submitError}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={deleteMutation.isPending}
            className="rounded border border-border-subtle px-4 py-2 text-sm hover:bg-bg-muted disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={deleteMutation.isPending}
            className="rounded bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-50"
          >
            {deleteMutation.isPending ? 'Removendo...' : 'Remover'}
          </button>
        </div>
      </div>
    </div>
  );
}
